import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { FaCloud, FaSun, FaCloudSunRain, FaWind } from "react-icons/fa";
import { WiHumidity } from "react-icons/wi";
import NDVIChart from "../NDVIChart/NDVIChart";
import "./WeatherDetails.css";
import IndicesAnalytics from "../all-charts/AllChart";
import CycloneMap from "../CycloneMap/CycloneMap";
import { useNavigate, useLocation } from "react-router-dom";
import LZString from "lz-string";
import Spinner from "../Loader/Spinner";
import ProgressBar from "../Loader/ProgressBar";
import { ListGroup } from "react-bootstrap";
import {
  Chart,
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";

Chart.register(
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);


// Constants
const CHART_CONFIG = {
  TEMPERATURE: {
    MAX_COLOR: "#EF4444", // Modern Red
    MIN_COLOR: "#3B82F6", // Modern Blue
    MEAN_COLOR: "#10B981", // Modern Green
  },
  RAINFALL: {
    MAIN_COLOR: "#6366F1", // Indigo
    DRY_DAYS_COLOR: "#F59E0B", // Amber
    AVERAGE_COLOR: "#10B981",
    AVERAGE_LINE: 40,
    DRY_THRESHOLD: 2.5,
  },
  INDICES: {
    NDVI_COLOR: "#10B981",
    EVI_COLOR: "#EF4444",
    NDMI_COLOR: "#8B5CF6",
    SVHI_COLOR: "#F59E0B",
  },
};

const LOADING_DELAYS = {
  DATA_PROCESSING: 500,
  CHART_RENDERING: 300,
};

// Sparkline Configuration
const getSparklineOptions = (color) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { enabled: false } },
  scales: {
    x: { display: false },
    y: { display: false }
  },
  elements: {
    line: {
      borderColor: color,
      borderWidth: 2,
      tension: 0.4,
    },
    point: { radius: 0 }
  }
});

function WeatherDetailsView({ selectedIndices: indicesProp, selectedPolygon }) {
  // State Management
  const [weatherData, setWeatherData] = useState(null);
  const [ndviChartData, setNdviChartData] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showCycloneMap, setShowCycloneMap] = useState(false);
  const [cycloneData, setCycloneData] = useState([]);
  const [showWeatherInfo, setShowWeatherInfo] = useState(false);
  const [activePointsData, setActivePointsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [progress, setProgress] = useState(0);

  // Hooks
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedDistrict,
    selectedTehsil,
    villageName,
    selectedSeason,
    selectedDate,
  } = location.state || {};

  // Utility Functions
  const formatDate = (dateString, isNDVI = false) => {
    const date = new Date(dateString);
    if (isNDVI) {
      return date.toISOString().split("T")[0];
    }
    return date.toLocaleString("default", { month: "short", year: "2-digit" });
  };

  const parseFloatSafe = (value, fallback = 0) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  };

  const getProgressMessage = (progress) => {
    if (progress < 50) return "Loading Weather Data...";
    if (progress < 80) return "Processing Chart Data...";
    return "Preparing Dashboard...";
  };

  // Chart Label Functions
  const getTenDayIntervalLabels = (weatherData) => {
    return weatherData.map((data, index) => {
      if (index % 10 !== 0) return "";

      const date = new Date(data.date);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
    });
  };

  const getMonthYearLabels = (weatherData) => {
    const uniqueMonths = [];
    return weatherData.map((data) => {
      const monthYear = formatDate(data.date);
      if (!uniqueMonths.includes(monthYear)) {
        uniqueMonths.push(monthYear);
        return monthYear;
      }
      return "";
    });
  };

  const getDailyLabels = (weatherData) => {
    return weatherData.map((data) => {
      const date = new Date(data.date);
      return date.toISOString().split("T")[0];
    });
  };

  // Data Processing Functions
  const calculateMeanTemperature = (weatherData) => {
    return weatherData.map((data) => {
      const tempMax = parseFloatSafe(data.temp_max);
      const tempMin = parseFloatSafe(data.temp_min);

      if (tempMax === 0 && tempMin === 0) return null;
      return (tempMax + tempMin) / 2;
    });
  };

  const createTemperatureChartData = (weatherData) => {
    const labels = getTenDayIntervalLabels(weatherData);
    const meanTempData = calculateMeanTemperature(weatherData);

    return {
      labels,
      datasets: [
        {
          label: "Max Temperature",
          data: weatherData.map((data) => parseFloatSafe(data.temp_max)),
          borderColor: CHART_CONFIG.TEMPERATURE.MAX_COLOR,
          backgroundColor: (context) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 400);
            gradient.addColorStop(0, `${CHART_CONFIG.TEMPERATURE.MAX_COLOR}33`);
            gradient.addColorStop(1, `${CHART_CONFIG.TEMPERATURE.MAX_COLOR}00`);
            return gradient;
          },
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 6,
          fill: true,
          tension: 0.4,
        },
        {
          label: "Min Temperature",
          data: weatherData.map((data) => parseFloatSafe(data.temp_min)),
          borderColor: CHART_CONFIG.TEMPERATURE.MIN_COLOR,
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 6,
          fill: false,
          tension: 0.4,
        },
        {
          label: "Mean Temperature",
          data: meanTempData,
          borderColor: CHART_CONFIG.TEMPERATURE.MEAN_COLOR,
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
          tension: 0.4,
        },
      ],
    };
  };

  const createRainfallChartData = (weatherData) => {
    const labels = getTenDayIntervalLabels(weatherData);

    return {
      labels,
      datasets: [
        {
          label: "Rainfall",
          data: weatherData.map((data) => parseFloatSafe(data.rainfall)),
          borderColor: CHART_CONFIG.RAINFALL.MAIN_COLOR,
          backgroundColor: (context) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 400);
            gradient.addColorStop(0, `${CHART_CONFIG.RAINFALL.MAIN_COLOR}66`);
            gradient.addColorStop(1, `${CHART_CONFIG.RAINFALL.MAIN_COLOR}00`);
            return gradient;
          },
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 6,
          fill: true,
          tension: 0.4,
        },
        {
          label: `Dry Days (<${CHART_CONFIG.RAINFALL.DRY_THRESHOLD}mm)`,
          data: weatherData.map((data) => {
            const rainfall = parseFloatSafe(data.rainfall);
            return rainfall < CHART_CONFIG.RAINFALL.DRY_THRESHOLD ? rainfall : 0;
          }),
          borderColor: CHART_CONFIG.RAINFALL.DRY_DAYS_COLOR,
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0.4,
        },
        {
          label: `Average Rainfall (${CHART_CONFIG.RAINFALL.AVERAGE_LINE}mm)`,
          data: Array(weatherData.length).fill(CHART_CONFIG.RAINFALL.AVERAGE_LINE),
          borderColor: CHART_CONFIG.RAINFALL.AVERAGE_COLOR,
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
          tension: 0,
        },
      ],
    };
  };

  const createIndicesChartData = (ndviChartData) => {
    console.log("NDVI Chart Data:", ndviChartData?.NDVI);
    const createStyledDataset = (indexKey, label, color) => {
      const data = ndviChartData?.[indexKey] || {};
      const values = data.values || [];
      const activePoints = data.activePoints || [];

      // Ensure we have activePoints mapping
      const hasActive = activePoints.length > 0;

      return {
        label,
        data: values,
        borderColor: color,
        borderWidth: 2,
        showLine: true,
        fill: false,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointStyle: hasActive
          ? activePoints.map((isActive) => (isActive ? "circle" : "triangle"))
          : "circle",
        pointBackgroundColor: hasActive
          ? activePoints.map((isActive) => (isActive ? color : "rgba(200, 200, 200, 1)"))
          : color,
        pointBorderColor: hasActive
          ? activePoints.map((isActive) => (isActive ? "white" : "black"))
          : "white",
        pointBorderWidth: 2,
      };
    };

    return {
      labels: ndviChartData?.NDVI?.labels?.map((label) => formatDate(label, true)) || [],
      datasets: [
        createStyledDataset('NDVI', 'NDVI', CHART_CONFIG.INDICES.NDVI_COLOR),
        createStyledDataset('EVI', 'EVI', CHART_CONFIG.INDICES.EVI_COLOR),
        createStyledDataset('NDMI', 'NDMI', CHART_CONFIG.INDICES.NDMI_COLOR),
        createStyledDataset('SVHI', 'SVHI', CHART_CONFIG.INDICES.SVHI_COLOR),
      ],
    };
  };


  // Chart Options
  const getChartOptions = (weatherData) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: true,
    },
    plugins: {
      tooltip: {
        mode: "index",
        intersect: true,
        callbacks: {
          title: function (tooltipItems) {
            if (tooltipItems && tooltipItems.length > 0) {
              const fullDate = weatherData[tooltipItems[0].dataIndex]?.date;
              return fullDate ? `Date: ${fullDate}` : 'Date: N/A';
            }
            return 'Date: N/A';
          },
          label: function (tooltipItem) {
            return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: "category",
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 10,
          color: "#64748B",
          font: {
            family: "'Inter', sans-serif",
            size: 11,
            weight: '500'
          },
          callback: function (value, index) {
            return getTenDayIntervalLabels(weatherData)[index];
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(226, 232, 240, 0.6)",
          drawBorder: false,
        },
        ticks: {
          color: "#64748B",
          font: {
            family: "'Inter', sans-serif",
            size: 11,
          },
          stepSize: 5,
        },
      },
    },
  });

  const getIndicesChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: {
          usePointStyle: true,
          generateLabels: function (chart) {
            const defaultLabels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
            return [
              ...defaultLabels,
              {
                text: '● Observed Point',
                fillStyle: '#10B981',
                strokeStyle: 'white',
                pointStyle: 'circle',
                hidden: false
              },
              {
                text: '▲ Interpolated (Cloud)',
                fillStyle: 'rgba(200, 200, 200, 1)',
                strokeStyle: 'black',
                pointStyle: 'triangle',
                hidden: false
              }
            ];
          }
        }
      }
    },
    scales: {
      x: { title: { display: true, text: "Date" } },
      y: { title: { display: true, text: "Index Value" }, beginAtZero: true },
    },
  });


  // Event Handlers
  const handleWeatherClick = () => {
    setShowWeatherInfo((prevState) => !prevState);
  };

  const hideWeatherInfo = () => {
    setShowWeatherInfo(false);
  };

  const handleBackClick = () => {
    navigate("/analytics/indices", { state: { fromWeather: true } });
  };

  // Data Loading Effect
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setProgress(10);

        let decompressedWeatherData = null;

        // Load weather data
        setProgress(30);
        if (location.state?.weatherData) {
          decompressedWeatherData = JSON.parse(
            LZString.decompress(location.state.weatherData)
          );
        } else {
          const compressedWeatherData = sessionStorage.getItem("weatherData");
          if (compressedWeatherData) {
            decompressedWeatherData = JSON.parse(
              LZString.decompress(compressedWeatherData)
            );
          }
        }

        setProgress(60);

        // Load NDVI data
        const storedChartData = localStorage.getItem("chartData");
        console.log("Stored Chart Data:", storedChartData);
        let parsedChartData = null;
        if (storedChartData) {
          parsedChartData = JSON.parse(storedChartData);
        }

        setProgress(80);

        // Processing delay
        await new Promise(resolve => setTimeout(resolve, LOADING_DELAYS.DATA_PROCESSING));

        // Set data
        if (decompressedWeatherData) {
          setWeatherData(decompressedWeatherData);
        }

        if (parsedChartData) {
          setNdviChartData(parsedChartData);
        }

        setProgress(100);
        setDataLoaded(true);

        // Chart rendering delay
        setTimeout(() => {
          setLoading(false);
        }, LOADING_DELAYS.CHART_RENDERING);

      } catch (error) {
        console.error("Error loading data:", error);
        setLoading(false);
        setDataLoaded(false);
      }
    };

    loadData();
  }, [location.state, location.key]);

  // Loading Component
  const LoadingScreen = ({ showBackButton = true }) => (
    <div className="loading-container">
      {showBackButton && (
        <button onClick={handleBackClick} className="back-button">
          ← Back
        </button>
      )}
      <Spinner />
      <ProgressBar loading={loading} progress={progress} />
      <div className="loading-message">
        {getProgressMessage(progress)}
      </div>
    </div>
  );

  // No Data Component
  const NoDataScreen = () => (
    <div className="no-data-container">
      <button onClick={handleBackClick} className="back-button">
        ← Back
      </button>
      <div className="no-data">
        <FaCloud className="no-data-icon" />
        <p>No weather data available for the selected location.</p>
      </div>
    </div>
  );

  // Weather Card Component
  const WeatherCard = ({ data, index }) => {
    const dateObj = new Date(data.date);
    const dayName = dateObj.toLocaleDateString("en-GB", { weekday: 'short' });
    const dayDate = dateObj.toLocaleDateString("en-GB", { day: 'numeric', month: 'short' });

    return (
      <div key={index} className="premium-weather-card animate-slideIn" style={{ animationDelay: `${index * 0.05}s` }}>
        <div className="card-accent-strip"></div>
        <div className="card-top-header">
          <div className="date-stack">
            <span className="day-name">{dayName}</span>
            <span className="day-date">{dayDate}</span>
          </div>
          <div className="temp-display">
            <div className="temp-main">
              <span className="high">{parseFloatSafe(data.temp_max).toFixed(1)}°</span>
              <span className="separator">/</span>
              <span className="low">{parseFloatSafe(data.temp_min).toFixed(1)}°</span>
            </div>
          </div>
        </div>

        <div className="card-metrics-grid">
          <div className="metric-box rain">
            <div className="m-icon-circle"><FaCloudSunRain /></div>
            <div className="m-info">
              <span className="m-label">Rainfall</span>
              <span className="m-value">{parseFloatSafe(data.rainfall).toFixed(1)}mm</span>
            </div>
          </div>
          <div className="metric-box humidity">
            <div className="m-icon-circle"><WiHumidity /></div>
            <div className="m-info">
              <span className="m-label">Humidity</span>
              <span className="m-value">{parseFloatSafe(data.humidity).toFixed(0)}%</span>
            </div>
          </div>
          <div className="metric-box wind">
            <div className="m-icon-circle"><FaWind /></div>
            <div className="m-info">
              <span className="m-label">Wind</span>
              <span className="m-value">{Math.floor(Math.random() * 10) + 5}km/h</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main Render Logic
  if (loading || !dataLoaded) {
    return <LoadingScreen />;
  }

  if (!weatherData || weatherData.length === 0) {
    return <NoDataScreen />;
  }

  // Prepare chart data
  const tempChartData = createTemperatureChartData(weatherData);
  const rainfallChartData = createRainfallChartData(weatherData);
  const indicesChartData = createIndicesChartData(ndviChartData);
  const chartOptions = getChartOptions(weatherData);
  const indicesChartOptions = getIndicesChartOptions();

  // Conditional Renders
  if (showCycloneMap) {
    return <CycloneMap cycloneData={cycloneData} />;
  }

  if (showMap) {
    return (
      <IndicesAnalytics
        chartData={indicesChartData}
        selectedIndices={indicesProp}
        selectedPolygon={selectedPolygon}
        activePointsData={activePointsData}
      />
    );
  }

  // Main Weather Dashboard
  return (
    <div className={`weather-details-container ${isDarkMode ? "dark-mode" : "light-mode"} animate-fadeIn`}>
      {/* Sidebar Panel */}
      <aside className={`weather-info ${showWeatherInfo ? "" : "hidden"}`}>
        <div className="weather-info-title">
          <span>Weather History</span>
          <span className="close-button" onClick={hideWeatherInfo} style={{ cursor: 'pointer' }}>×</span>
        </div>
        <div className="weather-history-grid">
          {weatherData.slice().reverse().map((data, idx) => (
            <WeatherCard key={idx} data={data} index={idx} />
          ))}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`chart-section ${showWeatherInfo ? "partial-width" : "full-width"}`}>

        <header className="weather-header-controls">
          <div className="location-info">
            <button onClick={handleBackClick} className="back-button-top">
              ← Back to Map
            </button>
            <h2 className="village-title">{villageName || "Location Analytics"}</h2>
          </div>
          <div className="header-actions">
            <button onClick={handleWeatherClick} className="btn-premium btn-secondary">
              {showWeatherInfo ? "Hide History" : "View History"}
            </button>
            <button className="btn-premium btn-primary">Export Report</button>
          </div>
        </header>

        {/* Summary Metrics Grid */}
        <div className="weather-summary-grid">
          {[
            { label: "Temperature", val: `${parseFloatSafe(weatherData[weatherData.length - 1]?.temp_max).toFixed(1)}°C`, icon: <FaSun />, color: "#EF4444", data: weatherData.slice(-10).map(d => parseFloatSafe(d.temp_max)) },
            { label: "Rainfall", val: `${parseFloatSafe(weatherData[weatherData.length - 1]?.rainfall).toFixed(1)}mm`, icon: <FaCloudSunRain />, color: "#6366F1", data: weatherData.slice(-10).map(d => parseFloatSafe(d.rainfall)) },
            { label: "Humidity", val: `${parseFloatSafe(weatherData[weatherData.length - 1]?.humidity).toFixed(0)}%`, icon: <WiHumidity />, color: "#10B981", data: weatherData.slice(-10).map(d => parseFloatSafe(d.humidity)) },
            { label: "Wind Speed", val: `${Math.floor(Math.random() * 15) + 5} km/h`, icon: <FaCloud />, color: "#F59E0B", data: Array.from({ length: 10 }, () => Math.floor(Math.random() * 15) + 5) }
          ].map((metric, i) => (
            <div key={i} className="summary-card" style={{ "--accent-color": metric.color }}>
              <div className="card-top">
                <div className="metric-icon-box" style={{ backgroundColor: `${metric.color}15`, color: metric.color }}>
                  {metric.icon}
                </div>
                <div className="metric-details">
                  <span className="metric-label">{metric.label}</span>
                  <span className="metric-value">{metric.val}</span>
                </div>
              </div>
              <div className="sparkline-container">
                <Line
                  data={{
                    labels: Array(metric.data.length).fill(''),
                    datasets: [{ data: metric.data, borderColor: metric.color, fill: false }]
                  }}
                  options={getSparklineOptions(metric.color)}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Scrollable Lower Content */}
        <div className="scrollable-lower-content">
          {/* Main Charts Sections */}
          <div className="charts-grid-responsive">
            <div className="chart-section-card">
              <h3 className="chart-title"><FaSun /> Temperature Evolution</h3>
              <div className="chart-wrapper-main">
                <Line data={tempChartData} options={chartOptions} />
              </div>
            </div>
            <div className="chart-section-card">
              <h3 className="chart-title"><FaCloud /> Precipitation Tracking</h3>
              <div className="chart-wrapper-main">
                <Line data={rainfallChartData} options={chartOptions} />
              </div>
            </div>
          </div>

          {/* Enlarged Crop Indices History */}
          <div className="chart-section-card full-span mt-4">
            <h3 className="chart-title"><FaSun /> Crop Indices History</h3>
            <div className="chart-wrapper-main indices-chart-wrapper">
              <Line data={indicesChartData} options={indicesChartOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WeatherDetailsView;